import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { BalanceAccountService } from 'src/app/share/services/balance-account/balance-account.service';
import { PaymentAccountsService } from 'src/app/share/services/payment-account/payment-accounts.service';

@Component({
  selector: 'app-balance-account-detail',
  standalone: false,
  templateUrl: './balance-account-detail.component.html',
  styleUrl: './balance-account-detail.component.scss',
})
export class BalanceAccountDetailComponent implements OnInit {
  protected readonly faSpinner = faSpinner;

  @Input() mode: 'ADD' | 'VIEW' | 'EDIT' = 'ADD';
  @Input() accountData: any = null;

  accountForm!: FormGroup;
  saveLoader: boolean = false;
  formErrorMessage: string = '';

  paymentAccounts: any[] = [];
  tableLoader: boolean = false;
  tableErrorMessage: string = '';

  colors: string[] = [
    '#0369A1', 
    '#A16207',
    '#7C3AED', 
    '#9D174D',
    '#047857',
    '#4F46E5',
    '#0D9488',
    '#DC2626',
    '#2563EB',
    '#EA580C'
  ];

  constructor(
    public readonly activeOffcanvas: NgbActiveOffcanvas,
    private readonly fb: FormBuilder,
    private readonly balanceAccountService: BalanceAccountService,
    private readonly paymentAccountsService: PaymentAccountsService
  ) {}

  page: number = 0;
  endOfResult: boolean = false;

  ngOnInit(): void {
    this.initForm();
    if (this.mode === 'VIEW') {
      this.page = 0;
      this.endOfResult = false;
      this.paymentAccounts = [];
      this.loadLinkedPaymentAccounts();
    }
  }

  initForm(): void {
    this.accountForm = this.fb.group({
      accountName: ['', [Validators.required, Validators.maxLength(50)]],
      accountNumber: ['', [Validators.required, Validators.pattern(/^\d{4}$/)]], // Last 4 digits only
      amount: [0, [Validators.required]],
      status: [true]
    });

    if (this.mode === 'VIEW' || this.mode === 'EDIT') {
      if (this.accountData) {
        this.accountForm.patchValue({
          accountName: (this.accountData.accountName || '').toUpperCase(),
          accountNumber: this.accountData.accountNumber,
          amount: this.accountData.amount || 0,
          status: this.accountData.status === 'Active'
        });

        if (this.mode === 'VIEW') {
          this.accountForm.disable();
        } else if (this.mode === 'EDIT') {
          this.accountForm.enable();
        }
      }
    }
  }

  onAccountNameInput(event: any): void {
    const input = event.target;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const upper = (input.value || '').toUpperCase();
    this.accountForm.get('accountName')?.setValue(upper, { emitEvent: false });
    // Restore selection cursor position
    setTimeout(() => {
      input.setSelectionRange(start, end);
    });
  }

  onAccountNumberInput(event: any): void {
    const input = event.target;
    const clean = input.value.replaceAll(/\D/g, ''); // Restrict to numbers only
    this.accountForm.get('accountNumber')?.setValue(clean, { emitEvent: false });
  }

  onNumberWheel(event: WheelEvent): void {
    event.preventDefault();
  }

  onNumberKeyDown(event: KeyboardEvent): void {
    if (['e', 'E', '+'].includes(event.key)) { // Allowed minus '-' key for negative values
      event.preventDefault();
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
    }
  }

  saveAccount(): void {
    if (this.accountForm.invalid) return;

    if (this.saveLoader) return;
    this.saveLoader = true;
    this.formErrorMessage = '';

    const rawValues = this.accountForm.getRawValue();

    if (this.mode === 'ADD') {
      const payload = {
        accountName: (rawValues.accountName || '').toUpperCase(),
        accountNumber: rawValues.accountNumber,
        amount: rawValues.amount
      };

      this.balanceAccountService.createNewBalanceAccount(payload).subscribe({
        next: (res: any) => {
          this.saveLoader = false;
          this.activeOffcanvas.close('SAVED');
        },
        error: (err: any) => {
          this.saveLoader = false;
          this.formErrorMessage = err?.error?.error?.message || err?.error?.message || 'Something went wrong';
        }
      });
    } else if (this.mode === 'EDIT' && this.accountData?._id) {
      const payload = {
        accountName: (rawValues.accountName || '').toUpperCase(),
        accountNumber: rawValues.accountNumber,
        amount: rawValues.amount,
        status: rawValues.status ? 'Active' : 'Inactive'
      };

      this.balanceAccountService.editBalanceAccount(this.accountData._id, payload).subscribe({
        next: (res: any) => {
          this.saveLoader = false;
          this.activeOffcanvas.close('SAVED');
        },
        error: (err: any) => {
          this.saveLoader = false;
          this.formErrorMessage = err?.error?.error?.message || err?.error?.message || 'Something went wrong';
        }
      });
    }
  }

  switchToEdit(): void {
    this.mode = 'EDIT';
    this.accountForm.enable();
  }

  formatReference(ref: string): string {
    if (!ref) return '-';
    ref = ref.trim();
    if (ref.startsWith('xxx')) return ref;
    if (ref.length > 4) {
      return 'xxx' + ref.slice(-4);
    }
    return ref;
  }

  getBadgeColor(id: string, index: number): string {
    const charCodeSum = id ? id.split('').reduce((sum: number, char: string) => sum + (char.codePointAt(0) ?? 0), 0) : index;
    const colorIndex = charCodeSum % this.colors.length;
    return this.colors[colorIndex];
  }

  loadLinkedPaymentAccounts() {
    if (!this.accountData?._id) return;
    this.tableLoader = true;
    this.tableErrorMessage = '';
    this.paymentAccountsService.getPaymentAccountList({ balanceAccountId: this.accountData._id, page: this.page }).subscribe({
      next: (res: any) => {
        this.tableLoader = false;
        const listData = res.data || [];
          this.paymentAccounts = [...this.paymentAccounts, ...listData];
        if (listData.length === 0 ) {
          this.endOfResult = true;
        }
      },
      error: (err: any) => {
        this.tableLoader = false;
        this.tableErrorMessage = err?.error?.error?.message || err?.error?.message || 'Failed to load payment accounts';
      }
    });
  }

  onScrollTable() {
    if (this.tableLoader || this.endOfResult) return;
    this.page++;
    this.loadLinkedPaymentAccounts();
  }
}
